import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../api";
import { BASE_URL } from "../../../commonUrls";
import "./SpendingInsights.css";

const INSIGHTS_URL = `${BASE_URL}/spending/monthly`;

function monthOptions(count = 6) {
  const now = new Date();
  const options = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    options.push({ value, label: d.toLocaleDateString(undefined, { month: "long", year: "numeric" }) });
  }
  return options;
}

export default function SpendingInsights() {
  const options = useMemo(() => monthOptions(8), []);
  const [month, setMonth] = useState(options[0]?.value);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    apiFetch(`${INSIGHTS_URL}?month=${month}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (!alive) return;
        setSummary(data);
      })
      .catch(() => {
        if (!alive) return;
        setError("Unable to load spending insights.");
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [month]);

  return (
    <div className="spending-page">
      <header className="spending-header">
        <div>
          <h1>Spending Insights</h1>
          <p>Category breakdown and daily trend for the month.</p>
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value)}>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </header>

      {loading ? (
        <p className="spending-muted">Loading insights...</p>
      ) : error ? (
        <p className="spending-muted">{error}</p>
      ) : (
        summary && (
          <>
            <div className="spending-metrics">
              <Metric label="Total spent" value={summary.totalSpent || 0} />
              <Metric label="Total income" value={summary.totalIncome || 0} />
              <Metric label="Net cashflow" value={summary.netCashflow || 0} />
            </div>

            <div className="spending-grid">
              <section className="spending-card">
                <h2>Category breakdown</h2>
                <CategoryChart data={summary.categoryBreakdown || []} />
              </section>
              <section className="spending-card">
                <h2>Daily trend</h2>
                <TrendChart data={summary.dailyTrend || []} />
              </section>
            </div>
          </>
        )
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="spending-metric">
      <div className="spending-metric-label">{label}</div>
      <div className="spending-metric-value">₹ {value}</div>
    </div>
  );
}

function CategoryChart({ data }) {
  if (!data.length) return <p className="spending-muted">No spend recorded.</p>;
  const max = Math.max(...data.map((d) => Number(d.amount || 0)), 1);
  return (
    <div className="category-chart">
      {data.slice(0, 8).map((item) => (
        <div key={item.categoryName} className="category-row">
          <div className="category-label">{item.categoryName}</div>
          <div className="category-bar">
            <span style={{ width: `${(Number(item.amount || 0) / max) * 100}%` }} />
          </div>
          <div className="category-value">₹ {item.amount || 0}</div>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ data }) {
  if (!data.length) return <p className="spending-muted">No spend recorded.</p>;
  const width = 600;
  const height = 200;
  const padding = 24;
  const values = data.map((d) => Number(d.amount || 0));
  const max = Math.max(...values, 1);
  const points = values.map((v, i) => {
    const x = padding + (i / Math.max(1, values.length - 1)) * (width - padding * 2);
    const y = height - padding - (v / max) * (height - padding * 2);
    return `${x},${y}`;
  });
  return (
    <svg className="trend-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Daily spend trend">
      <polyline points={points.join(" ")} fill="none" stroke="#0f172a" strokeWidth="2" />
      <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#e2e8f0" />
    </svg>
  );
}
